import { useCallback } from "react";
import { useLocalizationPreferences } from "~/lib/use-localization-preferences";
import { getCart, useSyncedCartField } from "~/lib/use-embedded-cart-global";

// Lowercase 3-letter code, matching checkoutdwa's shared cart cookie's `currency` field
// and its pricingCatalog rows (see use-embedded-cart.ts) — this is what selects which
// currency's catalog rows to display.
export type SupportedCurrency = "usd" | "cad";

function getCartCurrency(): SupportedCurrency | undefined {
  return getCart()?.state?.getPersist().currency as SupportedCurrency | undefined;
}

// Mirrors bouncieweb's useCartState: a single access point for this preference, kept in
// sync with the embedded cart's shared cookie via useSyncedCartField. Call this from
// anywhere you need the value — like useCartState, redundant simultaneous callers are
// fine; checkoutdwa's own cookie.js dedupes the underlying cookie-update listener to one
// regardless of subscriber count.
export function useCurrencyPreference(): [SupportedCurrency, (currency: SupportedCurrency) => void] {
  const [{ currency }, setPreferences] = useLocalizationPreferences();
  const setCurrency = useCallback((next: SupportedCurrency) => setPreferences({ currency: next }), [setPreferences]);
  useSyncedCartField([currency, setCurrency], getCartCurrency);
  return [currency, setCurrency];
}

// Only "usd"/"cad" have catalog data (see checkoutdwa's buildPricingCatalog.js), so any
// other region falls back to "usd" rather than returning an unmatched code.
export function getCurrencyFromBrowserLocale(locale: string | undefined): SupportedCurrency {
  if (!locale) return "usd";
  try {
    return new Intl.Locale(locale).maximize().region?.toLowerCase() === "ca" ? "cad" : "usd";
  } catch {
    return "usd";
  }
}
